import { useEffect, useId, useRef, useState } from 'react'

import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { ValidationMessage } from '../ValidationMessage/ValidationMessage'

import { Body, CharacterCount, Input, LiveRegion, Wrapper } from './styles'

interface Props {
  ariaLabelledby?: string
  disabled?: boolean
  errorMessage?: string
  maxCharacterCount: number
  name: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onInput?: (event: React.FormEvent<HTMLInputElement>) => void
  placeholder: string
  value: string | undefined
}

// Delay before announcing the character count so we speak once the user pauses,
// rather than fighting the per-keystroke echo on every letter.
const ANNOUNCE_DEBOUNCE_MS = 1000

export const EmbroideryTextInput: React.FC<Props> = ({
  ariaLabelledby,
  disabled,
  errorMessage,
  maxCharacterCount,
  name,
  onChange,
  onInput,
  placeholder,
  value,
}) => {
  const {
    magnolia: {
      general: {
        textCharacterCount: { charactersEntered },
      },
    },
  } = useTranslation()

  const errorId = useId()

  // The live region only receives text once typing settles
  // so it starts empty and never announces mid-burst.
  const [countAnnouncement, setCountAnnouncement] = useState('')
  const announceFrameRef = useRef<number | null>(null)

  useEffect(() => {
    if (!value) {
      // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change, react-you-might-not-need-an-effect/no-external-store-subscription
      setCountAnnouncement('')
      return
    }
    const message = charactersEntered
      .replace('{{count}}', String(value.length))
      .replace('{{max}}', String(maxCharacterCount))

    const timeoutId = setTimeout(() => {
      setCountAnnouncement('')
      announceFrameRef.current = requestAnimationFrame(() => setCountAnnouncement(message))
    }, ANNOUNCE_DEBOUNCE_MS)

    return () => {
      clearTimeout(timeoutId)
      if (announceFrameRef.current !== null) cancelAnimationFrame(announceFrameRef.current)
    }
  }, [value, maxCharacterCount, charactersEntered])

  return (
    <Wrapper disabled={disabled ?? false}>
      <Body hasValue={!!value} isInvalid={!!errorMessage}>
        <Input
          disabled={disabled ?? false}
          maxLength={maxCharacterCount}
          name={name}
          onChange={onChange}
          onInput={onInput}
          placeholder={placeholder}
          type='text'
          value={value}
          aria-labelledby={ariaLabelledby}
          aria-describedby={errorMessage ? errorId : undefined}
          aria-invalid={!!errorMessage}
        />

        {/* Visual count — hidden from AT to avoid the ambiguous "0/26" reading */}
        <CharacterCount disabled={disabled ?? false} aria-hidden>
          {value?.length ?? 0}/{maxCharacterCount}
        </CharacterCount>
      </Body>

      {/* Announces the count for screen readers once typing settles; empty on load. */}
      <LiveRegion role='status' aria-live='polite' aria-atomic='true'>
        {countAnnouncement}
      </LiveRegion>
      <ValidationMessage id={errorId} message={errorMessage} />
    </Wrapper>
  )
}
